import React, { useEffect } from 'react';

// How long the completion message stays up before the page reloads itself
const RELOAD_DELAY = 4000;

const DemoResetOverlay = ({ isLocked, isFinished }) => {
  // Lock scrolling, keep the keyboard out of the app behind the overlay and
  // warn before leaving while the reset is still running
  useEffect(() => {
    if (!isLocked) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const blockTab = (event) => {
      if (event.key === 'Tab') event.preventDefault();
    };

    const confirmUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('keydown', blockTab, true);
    if (!isFinished) window.addEventListener('beforeunload', confirmUnload);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', blockTab, true);
      window.removeEventListener('beforeunload', confirmUnload);
    };
  }, [isLocked, isFinished]);

  // Everything loaded before the reset is stale, so reload for the user
  useEffect(() => {
    if (!isLocked || !isFinished) return;

    const timeoutId = setTimeout(() => window.location.reload(), RELOAD_DELAY);

    return () => clearTimeout(timeoutId);
  }, [isLocked, isFinished]);

  if (!isLocked) return null;

  return (
    <div
      className='fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4'
      role='alertdialog'
      aria-modal='true'
      aria-busy={!isFinished}
    >
      <div className='flex flex-col items-center w-full max-w-[440px] bg-white rounded-lg p-6 text-center shadow-xl'>
        {isFinished ? (
          <>
            <span className='text-base font-medium text-neutral-800'>
              Demo reset complete
            </span>
            <p className='text-sm text-neutral-700 pt-2 leading-relaxed'>
              The demo data has been restored. Reloading the page...
            </p>
            <button
              onClick={() => window.location.reload()}
              className='bg-primary-500 text-white text-sm font-medium px-3 py-2 rounded-md mt-5'
            >
              Refresh now
            </button>
          </>
        ) : (
          <>
            <div className='w-10 h-10 rounded-full border-4 border-primary-100 border-t-primary-500 animate-spin' />
            <span className='text-base font-medium text-neutral-800 pt-4'>
              Resetting demo data
            </span>
            <p className='text-sm text-neutral-700 pt-2'>
              This may take a few minutes.
            </p>
            <p className='text-sm text-neutral-700 pt-2 leading-relaxed'>
              Closing this tab will not stop the reset. The app stays locked
              until it finishes.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default DemoResetOverlay;
